import React, { useState, useEffect } from "react";
import { supabase } from '../supabaseClient';
import "./Review.css";

const Review = () => {
  const [reviews, setReviews] = useState([]);
  const [newReview, setNewReview] = useState({
    name: "",
    rating: 0,
    comment: "",
    position: ""
  });
  const [hoverRating, setHoverRating] = useState(0);
  const [editingReview, setEditingReview] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [replyingTo, setReplyingTo] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const [loading, setLoading] = useState(true);
  const [showSuccessMessage, setShowSuccessMessage] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // Initialize current user
  useEffect(() => {
    const userIdentifier = localStorage.getItem('reviewUserIdentifier');
    if (!userIdentifier) {
      const newUserIdentifier = `user_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('reviewUserIdentifier', newUserIdentifier);
      setCurrentUser(newUserIdentifier);
    } else {
      setCurrentUser(userIdentifier);
    }
  }, []);

  // Load reviews from Supabase
  useEffect(() => {
    loadReviews();
  }, []);

  const loadReviews = async () => {
    try {
      setLoading(true);
      const { data, error } = await supabase
        .from('reviews')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      
      setReviews(data || []);
    } catch (error) {
      console.error('Error loading reviews:', error);
      setReviews([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    
    if (newReview.rating === 0) {
      alert("Please select a rating");
      return;
    }
    if (!newReview.name.trim()) {
      alert("Please enter your name");
      return;
    }
    if (!newReview.comment.trim()) {
      alert("Please share your experience");
      return;
    }

    setSubmitting(true);

    try {
      const reviewData = {
        name: newReview.name.trim(),
        rating: newReview.rating,
        comment: newReview.comment.trim(),
        position: newReview.position.trim(),
        user_id: currentUser,
        avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newReview.name.trim())}&background=1a365d&color=ffffff&bold=true&size=128`,
        likes: 0,
        dislikes: 0,
        liked_by: [],
        disliked_by: [],
        replies: [],
        verified: Math.random() > 0.7,
        created_at: new Date().toISOString()
      };

      const { data, error } = await supabase
        .from('reviews')
        .insert([reviewData])
        .select();

      if (error) throw error;

      setReviews([data[0], ...reviews]);
      setNewReview({ name: "", rating: 0, comment: "", position: "" });
      setHoverRating(0);
      setShowSuccessMessage(true);
      
      // Hide success message after 3 seconds
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    } catch (error) {
      console.error('Error submitting review:', error);
      alert("There was an error submitting your review. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleUpdateReview = async () => {
    if (!editingReview) return;
    
    if (newReview.rating === 0) {
      alert("Please select a rating");
      return;
    }
    if (!newReview.name.trim()) {
      alert("Please enter your name");
      return;
    }
    if (!newReview.comment.trim()) {
      alert("Please share your experience");
      return;
    }

    try {
      const { error } = await supabase
        .from('reviews')
        .update({
          name: newReview.name.trim(),
          rating: newReview.rating,
          comment: newReview.comment.trim(),
          position: newReview.position.trim(),
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newReview.name.trim())}&background=1a365d&color=ffffff&bold=true&size=128`
        })
        .eq('id', editingReview);

      if (error) throw error;

      setReviews(reviews.map(r => 
        r.id === editingReview ? {
          ...r,
          name: newReview.name.trim(),
          rating: newReview.rating,
          comment: newReview.comment.trim(),
          position: newReview.position.trim(),
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(newReview.name.trim())}&background=1a365d&color=ffffff&bold=true&size=128`
        } : r
      ));

      setNewReview({ name: "", rating: 0, comment: "", position: "" });
      setEditingReview(null);
      setShowSuccessMessage(true);
      
      setTimeout(() => {
        setShowSuccessMessage(false);
      }, 3000);
    } catch (error) {
      console.error('Error updating review:', error);
      alert("There was an error updating your review. Please try again.");
    }
  };

  const handleLike = async (reviewId) => {
    try {
      const review = reviews.find(r => r.id === reviewId);
      if (!review) return;

      const likedBy = review.liked_by || [];
      const dislikedBy = review.disliked_by || [];
      
      const hasLiked = likedBy.includes(currentUser);
      const hasDisliked = dislikedBy.includes(currentUser);
      
      let newLikes = review.likes || 0;
      let newDislikes = review.dislikes || 0;
      let newLikedBy = [...likedBy];
      let newDislikedBy = [...dislikedBy];

      if (hasLiked) {
        newLikes = Math.max(0, newLikes - 1);
        newLikedBy = newLikedBy.filter(id => id !== currentUser);
      } else {
        newLikes++;
        newLikedBy.push(currentUser);
        
        if (hasDisliked) {
          newDislikes = Math.max(0, newDislikes - 1);
          newDislikedBy = newDislikedBy.filter(id => id !== currentUser);
        }
      }

      const { error } = await supabase
        .from('reviews')
        .update({
          likes: newLikes,
          dislikes: newDislikes,
          liked_by: newLikedBy,
          disliked_by: newDislikedBy
        })
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(reviews.map(r => 
        r.id === reviewId ? {
          ...r,
          likes: newLikes,
          dislikes: newDislikes,
          liked_by: newLikedBy,
          disliked_by: newDislikedBy
        } : r
      ));
    } catch (error) {
      console.error('Error handling like:', error);
    }
  };

  const handleDislike = async (reviewId) => {
    try {
      const review = reviews.find(r => r.id === reviewId);
      if (!review) return;

      const likedBy = review.liked_by || [];
      const dislikedBy = review.disliked_by || [];
      
      const hasLiked = likedBy.includes(currentUser);
      const hasDisliked = dislikedBy.includes(currentUser);
      
      let newLikes = review.likes || 0;
      let newDislikes = review.dislikes || 0;
      let newLikedBy = [...likedBy];
      let newDislikedBy = [...dislikedBy];

      if (hasDisliked) {
        newDislikes = Math.max(0, newDislikes - 1);
        newDislikedBy = newDislikedBy.filter(id => id !== currentUser);
      } else {
        newDislikes++;
        newDislikedBy.push(currentUser);
        
        if (hasLiked) {
          newLikes = Math.max(0, newLikes - 1);
          newLikedBy = newLikedBy.filter(id => id !== currentUser);
        }
      }

      const { error } = await supabase
        .from('reviews')
        .update({
          likes: newLikes,
          dislikes: newDislikes,
          liked_by: newLikedBy,
          disliked_by: newDislikedBy
        })
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(reviews.map(r => 
        r.id === reviewId ? {
          ...r,
          likes: newLikes,
          dislikes: newDislikes,
          liked_by: newLikedBy,
          disliked_by: newDislikedBy
        } : r
      ));
    } catch (error) {
      console.error('Error handling dislike:', error);
    }
  };

  const handleReply = async (reviewId) => {
    if (!replyText.trim()) {
      alert("Please write a response");
      return;
    }

    try {
      const review = reviews.find(r => r.id === reviewId);
      if (!review) return;

      const newReply = {
        id: Date.now() + Math.random(),
        text: replyText.trim(),
        user_id: currentUser,
        date: new Date().toLocaleDateString('en-US', { 
          month: 'short', 
          day: 'numeric',
          year: 'numeric'
        }),
        timestamp: Date.now()
      };
      
      const existingReplies = review.replies || [];
      const updatedReplies = [...existingReplies, newReply];

      const { error } = await supabase
        .from('reviews')
        .update({ replies: updatedReplies })
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(reviews.map(r => 
        r.id === reviewId ? { ...r, replies: updatedReplies } : r
      ));
      
      setReplyText("");
      setReplyingTo(null);
    } catch (error) {
      console.error('Error adding reply:', error);
      alert("There was an error adding your response. Please try again.");
    }
  };

  const deleteReply = async (reviewId, replyId) => {
    try {
      const review = reviews.find(r => r.id === reviewId);
      if (!review) return;

      const existingReplies = review.replies || [];
      const updatedReplies = existingReplies.filter(reply => reply.id !== replyId);

      const { error } = await supabase
        .from('reviews')
        .update({ replies: updatedReplies })
        .eq('id', reviewId);

      if (error) throw error;

      setReviews(reviews.map(r => 
        r.id === reviewId ? { ...r, replies: updatedReplies } : r
      ));
    } catch (error) {
      console.error('Error deleting reply:', error);
    }
  };

  const handleDeleteReview = async (reviewId) => {
    if (window.confirm("Are you sure you want to delete this review?")) {
      try {
        const { error } = await supabase
          .from('reviews')
          .delete()
          .eq('id', reviewId);

        if (error) throw error;

        setReviews(reviews.filter(review => review.id !== reviewId));
      } catch (error) {
        console.error('Error deleting review:', error);
        alert("There was an error deleting the review. Please try again.");
      }
    }
  };

  // Star Rating Component
  const StarRating = ({ rating, onRatingChange, hoverRating, onHoverChange, readOnly = false }) => {
    return (
      <div className="star-rating">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className={`star-btn ${star <= (hoverRating || rating) ? "active" : ""}`}
            onClick={() => !readOnly && onRatingChange(star)}
            onMouseEnter={() => !readOnly && onHoverChange(star)}
            onMouseLeave={() => !readOnly && onHoverChange(0)}
            disabled={readOnly}
            style={readOnly ? { cursor: 'default' } : {}}
          >
            ★
          </button>
        ))}
        {!readOnly && (
          <span className="rating-text">
            {rating > 0 ? `${rating}.0` : "Select Rating"}
          </span>
        )}
      </div>
    );
  };

  const DisplayStars = ({ rating }) => {
    return (
      <div className="rating-stars">
        {[...Array(5)].map((_, index) => (
          <span
            key={index}
            className={`star-display ${index < rating ? "active" : ""}`}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  const isUserReview = (review) => {
    return review.user_id === currentUser;
  };

  const filteredReviews = reviews.filter(review => {
    if (activeTab === "5-star") return review.rating === 5;
    if (activeTab === "4-star") return review.rating === 4;
    if (activeTab === "my-reviews") return isUserReview(review);
    return true;
  });

  const averageRating = reviews.length > 0 
    ? (reviews.reduce((acc, review) => acc + (review.rating || 0), 0) / reviews.length).toFixed(1)
    : "0.0";

  if (loading) {
    return (
      <section className="review-section">
        <div className="review-container">
          <div className="loading-state">
            <div className="loading-spinner"></div>
            <p>Loading reviews...</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section className="review-section">
      {/* Success Message */}
      {showSuccessMessage && (
        <div className="success-message">
          <i className="bi bi-check-circle"></i>
          {editingReview ? "Review updated successfully!" : "Thank you for sharing your experience!"}
        </div>
      )}

      {/* Hero Banner */}
      <div className="review-hero">
        <div className="review-container">
          <div className="hero-content"><br></br>
            
            <h1 className="review-title">
               Client Testimonials<br></br>
              What Our Clients Say
            </h1>
            <p className="review-subtitle">
              Real stories of healing and transformation from our counseling center
            </p>
            
            <div className="stats-overview">
              <div className="stat-item">
                <div className="stat-number">{reviews.length}</div>
                <div className="stat-label">Total Reviews</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">{averageRating}</div>
                <div className="stat-label">Average Rating</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  {reviews.filter(r => r.rating >= 4).length}
                </div>
                <div className="stat-label">Positive</div>
              </div>
              <div className="stat-item">
                <div className="stat-number">
                  {reviews.filter(r => r.verified).length}
                </div>
                <div className="stat-label">Verified</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Review Content Section */}
      <div className="review-content-section">
        <div className="review-container">
          <div className="review-content-wrapper">
            {/* Review Form */}
            <div className="review-form-container">
              <div className="form-card">
                <div className="form-header">
                  <h3 className="form-title">
                    {editingReview ? "Edit Your Review" : "Share Your Experience"}
                  </h3>
                  <p className="form-subtitle">
                    Your story can inspire others on their healing journey
                  </p>
                </div>
                
                <form onSubmit={editingReview ? (e) => { e.preventDefault(); handleUpdateReview(); } : handleSubmitReview} className="review-form">
                  <div className="form-group">
                    <label htmlFor="name">
                      Full Name <span className="required">*</span>
                    </label>
                    <input
                      type="text"
                      id="name"
                      value={newReview.name}
                      onChange={(e) => setNewReview({ ...newReview, name: e.target.value })}
                      placeholder="Enter your full name"
                      required
                      maxLength={50}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="position">Your Role (Optional)</label>
                    <input
                      type="text"
                      id="position"
                      value={newReview.position}
                      onChange={(e) => setNewReview({ ...newReview, position: e.target.value })}
                      placeholder="e.g., Student, Professional, Parent"
                      maxLength={30}
                      className="form-control"
                    />
                  </div>

                  <div className="form-group">
                    <label>
                      Your Rating <span className="required">*</span>
                    </label>
                    <StarRating
                      rating={newReview.rating}
                      onRatingChange={(rating) => setNewReview({ ...newReview, rating })}
                      hoverRating={hoverRating}
                      onHoverChange={setHoverRating}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="comment">
                      Your Experience <span className="required">*</span>
                    </label>
                    <textarea
                      id="comment"
                      value={newReview.comment}
                      onChange={(e) => setNewReview({ ...newReview, comment: e.target.value })}
                      placeholder="Share how counseling has impacted your life, what you appreciated, and any advice for others..."
                      rows="4"
                      required
                      maxLength={500}
                      className="form-control"
                    ></textarea>
                    <div className="char-count">
                      {newReview.comment.length}/500 characters
                    </div>
                  </div>

                  <button type="submit" className="submit-btn" disabled={submitting}>
                    <i className={`bi ${editingReview ? "bi-check-lg" : "bi-send"}`}></i>
                    <span>
                      {submitting ? "Processing..." : editingReview ? "Update Review" : "Submit Review"}
                    </span>
                  </button>
                  
                  {editingReview && (
                    <button 
                      type="button"
                      className="cancel-reply"
                      onClick={() => {
                        setEditingReview(null);
                        setNewReview({ name: "", rating: 0, comment: "", position: "" });
                      }}
                      style={{ marginTop: '12px', width: '100%' }}
                    >
                      Cancel Edit
                    </button>
                  )}
                </form>
              </div>
            </div>

            {/* Reviews Display */}
            <div className="reviews-display">
              <div className="display-header">
                <div className="filter-tabs">
                  <button 
                    className={`filter-tab ${activeTab === "all" ? "active" : ""}`}
                    onClick={() => setActiveTab("all")}
                  >
                    All ({reviews.length})
                  </button>
                  <button 
                    className={`filter-tab ${activeTab === "5-star" ? "active" : ""}`}
                    onClick={() => setActiveTab("5-star")}
                  >
                    5★ ({reviews.filter(r => r.rating === 5).length})
                  </button>
                  <button 
                    className={`filter-tab ${activeTab === "4-star" ? "active" : ""}`}
                    onClick={() => setActiveTab("4-star")}
                  >
                    4★ ({reviews.filter(r => r.rating === 4).length})
                  </button>
                  <button 
                    className={`filter-tab ${activeTab === "my-reviews" ? "active" : ""}`}
                    onClick={() => setActiveTab("my-reviews")}
                  >
                    My ({reviews.filter(r => r.user_id === currentUser).length})
                  </button>
                </div>
                
                <div className="sort-controls">
                  <span className="results-count">
                    Showing {filteredReviews.length} {filteredReviews.length === 1 ? 'review' : 'reviews'}
                  </span>
                </div>
              </div>
              
              {filteredReviews.length === 0 ? (
                <div className="no-reviews">
                  <div className="no-reviews-icon">
                    <i className="bi bi-chat-left-text"></i>
                  </div>
                  <h4>No Reviews Found</h4>
                  <p>
                    {activeTab === "my-reviews" 
                      ? "You haven't submitted any reviews yet. Share your healing journey!" 
                      : activeTab === "5-star" || activeTab === "4-star"
                      ? `No ${activeTab} reviews yet. Be the first to share your experience!`
                      : "Be the first to share your healing journey and inspire others!"}
                  </p>
                  {activeTab !== "all" && (
                    <button 
                      className="filter-tab"
                      onClick={() => setActiveTab("all")}
                      style={{ marginTop: '16px', padding: '10px 24px' }}
                    >
                      View All Reviews
                    </button>
                  )}
                </div>
              ) : (
                <div className="reviews-grid">
                  {filteredReviews.map((review) => (
                    <div key={review.id} className="review-card">
                      <div className="review-card-header">
                        <div className="reviewer-info">
                          <img 
                            src={review.avatar} 
                            alt={review.name} 
                            className="reviewer-avatar"
                            onError={(e) => {
                              e.target.src = `https://ui-avatars.com/api/?name=Client&background=1a365d&color=ffffff&bold=true&size=128`;
                            }}
                          />
                          <div className="reviewer-details">
                            <div className="reviewer-name-wrapper">
                              <h4 className="reviewer-name">{review.name}</h4>
                              {review.verified && (
                                <span className="verified-badge" title="Verified Client">
                                  <i className="bi bi-patch-check-fill"></i>
                                </span>
                              )}
                              {isUserReview(review) && (
                                <span className="your-review-badge">
                                  <i className="bi bi-person-circle"></i> You
                                </span>
                              )}
                            </div>
                            {review.position && (
                              <p className="reviewer-position">{review.position}</p>
                            )}
                            <span className="review-date">
                              <i className="bi bi-calendar"></i> {new Date(review.created_at).toLocaleDateString('en-US', {
                                month: 'short',
                                day: 'numeric',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                        </div>
                        <div className="review-rating">
                          <DisplayStars rating={review.rating} />
                          <span className="rating-number">{review.rating}.0</span>
                        </div>
                      </div>
                      
                      <div className="review-content">
                        <p className="review-comment">"{review.comment}"</p>
                      </div>

                      <div className="review-engagement">
                        <div className="engagement-metrics">
                          <div className="metric-item">
                            <button 
                              className={`like-btn ${(review.liked_by || []).includes(currentUser) ? 'active' : ''}`}
                              onClick={() => handleLike(review.id)}
                            >
                              <i className="bi bi-hand-thumbs-up"></i>
                              <span>{review.likes || 0}</span>
                            </button>
                          </div>
                          <div className="metric-item">
                            <button 
                              className={`dislike-btn ${(review.disliked_by || []).includes(currentUser) ? 'active' : ''}`}
                              onClick={() => handleDislike(review.id)}
                            >
                              <i className="bi bi-hand-thumbs-down"></i>
                              <span>{review.dislikes || 0}</span>
                            </button>
                          </div>
                          <div className="metric-item">
                            <button 
                              className="reply-btn"
                              onClick={() => setReplyingTo(replyingTo === review.id ? null : review.id)}
                            >
                              <i className="bi bi-reply"></i>
                              <span>Reply</span>
                            </button>
                          </div>
                        </div>

                        {isUserReview(review) && (
                          <div className="review-actions">
                            <button 
                              className="edit-btn"
                              onClick={() => {
                                setEditingReview(review.id);
                                setNewReview({
                                  name: review.name,
                                  rating: review.rating,
                                  comment: review.comment,
                                  position: review.position || ""
                                });
                              }}
                              title="Edit review"
                            >
                              <i className="bi bi-pencil"></i> Edit
                            </button>
                            <button 
                              className="delete-btn"
                              onClick={() => handleDeleteReview(review.id)}
                              title="Delete review"
                            >
                              <i className="bi bi-trash"></i> Delete
                            </button>
                          </div>
                        )}
                      </div>

                      {/* Reply Input */}
                      {replyingTo === review.id && (
                        <div className="reply-section">
                          <div className="reply-input">
                            <textarea
                              value={replyText}
                              onChange={(e) => setReplyText(e.target.value)}
                              placeholder="Write your response..."
                              rows="2"
                              className="reply-textarea"
                              maxLength={200}
                            />
                            <div className="reply-actions">
                              <button 
                                className="cancel-reply"
                                onClick={() => {
                                  setReplyingTo(null);
                                  setReplyText("");
                                }}
                              >
                                Cancel
                              </button>
                              <button 
                                className="submit-reply"
                                onClick={() => handleReply(review.id)}
                                disabled={!replyText.trim()}
                              >
                                Post Response
                              </button>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Replies List */}
                      {(review.replies || []).length > 0 && (
                        <div className="replies-list">
                          <div className="replies-header">
                            <i className="bi bi-chat-left-text"></i>
                            <span>Responses ({(review.replies || []).length})</span>
                          </div>
                          {(review.replies || []).map((reply) => (
                            <div key={reply.id} className="reply-item">
                              <p className="reply-text">{reply.text}</p>
                              <div className="reply-meta">
                                <span className="reply-date">
                                  <i className="bi bi-clock"></i> {reply.date}
                                </span>
                                {reply.user_id === currentUser && (
                                  <button 
                                    className="delete-reply"
                                    onClick={() => deleteReply(review.id, reply.id)}
                                    title="Delete response"
                                  >
                                    <i className="bi bi-trash"></i>
                                  </button>
                                )}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Review;